import { ReactNode, useCallback, useEffect } from 'react';
import AppShell from '@components/layout/app-shell/app-shell.tsx';
import SideNav from '@components/layout/sidebar/side-nav.tsx';
import ResponsiveSideNav from '@components/layout/responsive-side-nav.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import * as Sentry from '@sentry/react';
import MaintenanceBanner from '@components/layout/maintenance-banner.tsx';
import UploadSuspendedBanner from '@components/layout/upload-suspended-banner.tsx';
import ActingAsBanner from '@components/layout/acting-as-banner.tsx';
import { ActingAsBanner as SharedActingAsBanner } from '@components/shared/acting-as-banner/acting-as-banner.tsx';
import { useGetAdminSettings } from '@utils/api/admin/use-get-admin-settings.ts';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { getHomeOrg } from '@utils/extensions/home-org.extension.ts';
import { IS_ON_PREM } from '@utils/constants/app-constants';
import TopBar from '@components/layout/top-bar.tsx';
import { TopBarSlotsProvider } from '@components/layout/top-bar-context.tsx';
import { useSidebarCollapsed } from '@utils/hooks/use-sidebar-collapsed.ts';
import PublishJobsWatcher from '@components/publishing/publish-jobs-watcher.tsx';

interface Props {
  layoutType?: 'main' | 'editor';
  children: ReactNode;
  disableOverflowY?: boolean;
}

export default function PrivatePageWrapper(props: Props) {
  const { data: organization } = useOrganization();
  const { isSysOp, isReseller } = useRoles();

  const switcherToken = TokenExtension.get();
  const switcherUid = switcherToken?.decoded?.uid;
  const switcherOid = switcherToken?.decoded?.oid?.toString() ?? '';
  const resellerHome = switcherUid ? getHomeOrg('reseller', switcherUid) : null;
  const isResellerImpersonating =
    !!resellerHome && resellerHome.orgId !== switcherOid;

  const { data: settingsData } = useGetAdminSettings();
  const isMaintenanceMode =
    IS_ON_PREM && isSysOp && settingsData?.result?.maintenance_mode === 'true';

  useEffect(() => {
    if (
      import.meta.env.VITE_NODE_ENV === 'production' &&
      organization?.id &&
      organization.name
    ) {
      Sentry.setTags({
        org_id: String(organization.id),
        org_name: organization.name
      });
      Sentry.setContext('organization', {
        id: organization.id,
        name: organization.name,
        email: organization.email ?? ''
      });
    }
  }, [organization?.id, organization?.name, organization?.email]);

  const isEditor = props.layoutType === 'editor';
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed();

  const handleToggleCollapsed = useCallback(
    () => setSidebarCollapsed(!sidebarCollapsed),
    [sidebarCollapsed, setSidebarCollapsed]
  );

  return (
    <TopBarSlotsProvider>
      <AppShell
        sidebar={
          <SideNav
            collapsed={isEditor && sidebarCollapsed}
            onToggleCollapsed={isEditor ? handleToggleCollapsed : undefined}
          />
        }
        mobileSidebar={(open, setOpen) => (
          <ResponsiveSideNav sidebarOpen={open} setSidebarOpen={setOpen} />
        )}
        layoutType={props.layoutType}
        disableOverflowY={props.disableOverflowY}
        topBar={<TopBar />}
        banners={
          <>
            <MaintenanceBanner isVisible={isMaintenanceMode ?? false} />
            <UploadSuspendedBanner />
          </>
        }
      >
        {props.children}
      </AppShell>
      {!isResellerImpersonating && <ActingAsBanner />}
      <SharedActingAsBanner
        enabled={!!isReseller && !isSysOp}
        homeOrgKeyPrefix="reseller"
        backLink="/reseller"
        backLinkKey="back-to-reseller-dashboard"
        bannerMessageKey="acting-as-banner-reseller"
      />
      <PublishJobsWatcher />
    </TopBarSlotsProvider>
  );
}
